import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { SwapFigureIcon } from '~/components/icons'
import {
  MEASURES,
  NUTRIENT_COLORS,
  NUTRIENT_LABELS,
  NUTRIENT_UNITS,
  TARGETED,
  amountForNutrient,
  nutrientsFor,
  takesAWeight,
  unitsOf,
  type Amount,
  type Food,
  type Targeted,
} from '~/data/nutrition'

/**
 * The rows a food is logged from, along the foot of its page — the nutrition spec, Logging an
 * entry, as the phone lays it out. Three of them, each about one thing: the amount typed big
 * with its unit behind it, and beside it the figure being typed instead where one is; under
 * that the control that swaps between the two and the units the amount can be counted in; and
 * under that what the page hands in as `beside` — when the entry lands, and the key that logs.
 *
 * They are the foot's own rows and wear no ground of their own: the foot is already a panel,
 * and a panel inside a panel drew a box around half of what the foot holds and left the other
 * half outside it.
 */
export function AmountBar({
  food,
  amount,
  onAmount,
  primary,
  onPrimary,
  beside,
  busy = false,
}: {
  food: Food
  amount: Amount
  onAmount: (next: Amount) => void
  /** What the key says; none where the page places the key itself. */
  primary?: string | undefined
  onPrimary?: (() => void) | undefined
  /** What stands at the end of the second row, after the units. */
  beside?: ReactNode
  /** Work the page is waiting on — the key says so, and neither it nor Enter presses again. */
  busy?: boolean
}) {
  const busyShown = useBeat(busy)
  const { units: accountUnits } = useAuth()
  const field = useRef<HTMLInputElement>(null)
  const [solving, setSolving] = useState(false)
  const [figure, setFigure] = useState<Targeted>('calories')
  /** What has been typed into the figure, kept apart so it reads back as typed. */
  const [typed, setTyped] = useState<string | null>(null)

  useEffect(() => {
    field.current?.focus()
    field.current?.select()
  }, [food])

  const units: { key: string; label: string; group: string; amount: Amount }[] = [
    ...(takesAWeight(food)
      ? unitsOf(food, accountUnits).map((unit) => ({
          key: unit,
          label: unit,
          group: MEASURES[unit].measure,
          amount: { value: amount.value, unit, portion: null },
        }))
      : []),
    ...food.portions.map((portion) => ({
      key: `portion:${portion.name}`,
      label: takesAWeight(food)
        ? `${portion.name} (${String(Math.round(portion.amount * 100) / 100)} ${portion.unit})`
        : portion.name,
      group: 'portion',
      amount: { value: amount.value, unit: 'portion' as const, portion: portion.name },
    })),
  ]
  const chosenKey = amount.unit === 'portion' ? `portion:${amount.portion ?? ''}` : amount.unit
  const unitSaid = amount.unit === 'portion' ? (amount.portion ?? 'portion') : amount.unit
  const comesTo = nutrientsFor(food, amount)
  const held = comesTo?.[figure]
  const figureShown =
    typed ?? (held === null || held === undefined ? '' : String(Math.round(held * 100) / 100))
  const wanted = typed === null ? null : Number(typed)
  const unsolvable =
    solving &&
    typed !== null &&
    typed.trim() !== '' &&
    wanted !== null &&
    Number.isFinite(wanted) &&
    amountForNutrient(food, figure, wanted, amount.unit, amount.portion) === null

  const typeFigure = (text: string) => {
    setTyped(text)
    const read = Number(text)
    if (text.trim() === '' || !Number.isFinite(read)) {
      onAmount({ ...amount, value: 0 })
      return
    }
    const solved = amountForNutrient(food, figure, read, amount.unit, amount.portion)
    if (solved !== null) {
      onAmount({ ...amount, value: solved })
    }
  }

  const chip = (on: boolean) =>
    `flex h-8 shrink-0 items-center rounded-full px-3.5 text-sm transition-colors ${
      on
        ? 'bg-accent/15 text-accent ring-1 ring-accent/40 ring-inset'
        : 'text-ink-muted ring-1 ring-white/15 ring-inset hover:bg-hover hover:text-ink'
    }`

  return (
    <div aria-label="Amount" className="space-y-2">
      <div className="flex items-stretch gap-2">
        {/* The figure being typed instead, beside the amount it arrives at. */}
        <div
          className={`grid transition-[grid-template-columns] duration-200 ${solving ? 'grid-cols-[1fr]' : 'grid-cols-[0fr]'}`}
        >
          <label className="relative block min-w-0 overflow-hidden">
            <input
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              aria-label={NUTRIENT_LABELS[figure]}
              tabIndex={solving ? 0 : -1}
              value={figureShown}
              onChange={(event) => {
                typeFigure(event.target.value)
              }}
              className="w-32 rounded-2xl bg-surface py-2 pr-12 pl-3 text-2xl outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
            />
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-ink-muted">
              {NUTRIENT_UNITS[figure]}
            </span>
          </label>
        </div>
        <label className="relative block min-w-0 flex-1">
          <input
            ref={field}
            type="number"
            inputMode="decimal"
            min="0"
            step="any"
            aria-label="Amount"
            value={amount.value === 0 ? '' : String(Math.round(amount.value * 100) / 100)}
            onChange={(event) => {
              setTyped(null)
              const read = Number(event.target.value)
              onAmount({
                ...amount,
                value: event.target.value.trim() === '' || !Number.isFinite(read) ? 0 : read,
              })
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && amount.value > 0 && !busy) {
                onPrimary?.()
              }
            }}
            className="w-full rounded-2xl bg-surface py-2 pr-20 pl-3 text-2xl outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
          />
          <span className="pointer-events-none absolute inset-y-0 right-3 flex max-w-24 items-center truncate text-sm text-ink-muted">
            {unitSaid}
          </span>
        </label>
        {primary !== undefined && (
          <button
            type="button"
            aria-busy={busy || undefined}
            disabled={amount.value <= 0}
            className="relative shrink-0 rounded-2xl bg-accent px-5 text-sm font-medium text-accent-ink disabled:opacity-50"
            onClick={() => {
              if (!busy) onPrimary?.()
            }}
          >
            <Working shown={busyShown}>{primary}</Working>
          </button>
        )}
      </div>
      {unsolvable && <p className="text-xs text-danger">No amount of {food.name} comes to that.</p>}
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Type a figure instead"
          aria-pressed={solving}
          className={`shrink-0 rounded-full p-2 transition-colors hover:bg-hover ${solving ? 'text-accent' : 'text-ink-muted'}`}
          onClick={() => {
            setTyped(null)
            setSolving((held) => !held)
          }}
        >
          <SwapFigureIcon aria-hidden className="size-5" />
        </button>
        {solving ? (
          <div
            role="radiogroup"
            aria-label="What is being typed"
            className="flex min-w-0 shrink gap-2 overflow-x-auto"
          >
            {TARGETED.map((each) => (
              <button
                key={each}
                type="button"
                role="radio"
                aria-checked={each === figure}
                aria-label={`Type the ${NUTRIENT_LABELS[each].toLowerCase()}`}
                className={chip(each === figure)}
                style={each === figure ? undefined : { color: NUTRIENT_COLORS[each] }}
                onClick={() => {
                  setFigure(each)
                  setTyped(null)
                }}
              >
                {NUTRIENT_LABELS[each]}
              </button>
            ))}
          </div>
        ) : (
          <div
            role="radiogroup"
            aria-label="Unit"
            className="flex min-w-0 shrink gap-2 overflow-x-auto"
          >
            {units.map((unit, at) => (
              <Fragment key={unit.key}>
                {at > 0 && units[at - 1]?.group !== unit.group && (
                  <span aria-hidden className="my-1 w-px shrink-0 self-stretch bg-white/15" />
                )}
                <button
                  type="button"
                  role="radio"
                  aria-checked={unit.key === chosenKey}
                  className={chip(unit.key === chosenKey)}
                  onClick={() => {
                    setTyped(null)
                    onAmount(unit.amount)
                  }}
                >
                  {unit.label}
                </button>
              </Fragment>
            ))}
          </div>
        )}
      </div>
      {/* When it lands, and the key: their own row, so neither is read as a unit chip. */}
      {beside !== undefined && <div className="flex items-center gap-3">{beside}</div>}
    </div>
  )
}
